/**
 * ============================================================================
 * MENTAL LOAD CATALOG v2.1
 * ============================================================================
 *
 * 50 vordefinierte Mental Load Aufgaben mit Beschreibungen und Subtasks.
 * Basiert auf dem Mental Load Konzept für Haushaltsmanagement.
 *
 * Features:
 * - 8 Kategorien mit je 5-8 Aufgaben
 * - Beschreibung pro Aufgabe
 * - Subtasks pro Aufgabe
 * - Suchfunktion
 * - 22 häufig verwendete Aufgaben (frequent: true)
 *
 * Kategorien:
 * - 🏠 Haushalt (4 Favoriten)
 * - 👶 Kinder (5 Favoriten)
 * - 💰 Finanzen (3 Favoriten)
 * - ❤️ Gesundheit (4 Favoriten)
 * - 📆 Termine & Events (3 Favoriten)
 * - 👨‍👩‍👧‍👦 Soziales & Familie (1 Favorit)
 * - 🚗 Auto & Mobilität (1 Favorit)
 * - 📌 Sonstiges (3 Favoriten)
 *
 * @version 2.1.0
 * @updated 2026-01-16
 */

// ============================================================================
// MENTAL LOAD KATALOG
// ============================================================================
export const MENTAL_LOAD_CATALOG = {
  'haushalt': {
    id: 'haushalt',
    label: 'Haushalt',
    emoji: '🏠',
    tasks: [
      {
        id: 'ml-1',
        name: 'Wäsche waschen & aufhängen',
        emoji: '🧺',
        frequent: true,
        description: 'Wäsche sortieren, waschen, aufhängen und zusammenlegen.',
        subtasks: [
          { title: 'Wäsche nach Farben sortieren', done: 0 },
          { title: 'Waschmaschine befüllen & starten', done: 0 },
          { title: 'Wäsche aufhängen/in Trockner', done: 0 },
          { title: 'Trockene Wäsche zusammenlegen', done: 0 },
          { title: 'Wäsche in Schränke einräumen', done: 0 }
        ]
      },
      {
        id: 'ml-2',
        name: 'Einkaufsliste erstellen',
        emoji: '📝',
        frequent: true,
        description: 'Vorräte checken und Einkaufsliste für die Woche erstellen.',
        subtasks: [
          { title: 'Kühlschrank & Vorräte checken', done: 0 },
          { title: 'Wochenplan/Rezepte durchgehen', done: 0 },
          { title: 'Fehlende Zutaten notieren', done: 0 },
          { title: 'Haushaltsbedarf prüfen', done: 0 },
          { title: 'Liste nach Kategorien sortieren', done: 0 }
        ]
      },
      {
        id: 'ml-3',
        name: 'Kühlschrank checken & auffüllen',
        emoji: '🧊',
        frequent: true,
        description: 'Kühlschrankinhalt prüfen, abgelaufene Produkte entsorgen.',
        subtasks: [
          { title: 'Abgelaufene Produkte entsorgen', done: 0 },
          { title: 'Reste verwerten oder einfrieren', done: 0 },
          { title: 'Fehlende Basics notieren', done: 0 },
          { title: 'Kühlschrank auswischen', done: 0 }
        ]
      },
      {
        id: 'ml-4',
        name: 'Müll rausbringen',
        emoji: '🗑️',
        frequent: false,
        description: 'Müll trennen und zur Abholung bereitstellen.',
        subtasks: [
          { title: 'Restmüll rausbringen', done: 0 },
          { title: 'Papiermüll entsorgen', done: 0 },
          { title: 'Gelber Sack/Plastik rausbringen', done: 0 },
          { title: 'Glascontainer-Fahrt planen', done: 0 },
          { title: 'Neue Müllbeutel einsetzen', done: 0 }
        ]
      },
      {
        id: 'ml-5',
        name: 'Wohnung aufräumen',
        emoji: '🧹',
        frequent: true,
        description: 'Allgemeines Aufräumen und Ordnung schaffen.',
        subtasks: [
          { title: 'Wohnzimmer aufräumen', done: 0 },
          { title: 'Küche aufräumen & Spülmaschine', done: 0 },
          { title: 'Bad aufräumen', done: 0 },
          { title: 'Schlafzimmer ordnen', done: 0 },
          { title: 'Staubsaugen/Wischen', done: 0 }
        ]
      },
      {
        id: 'ml-6',
        name: 'Staub wischen',
        emoji: '🧽',
        frequent: false,
        description: 'Oberflächen abstauben und Möbel pflegen.',
        subtasks: [
          { title: 'Regale abstauben', done: 0 },
          { title: 'Fernseher & Elektronik', done: 0 },
          { title: 'Bilderrahmen & Deko', done: 0 },
          { title: 'Fensterbänke wischen', done: 0 },
          { title: 'Lampen & Leuchten', done: 0 }
        ]
      },
      {
        id: 'ml-7',
        name: 'Fenster putzen',
        emoji: '🪟',
        frequent: false,
        description: 'Fenster und Glasflächen reinigen.',
        subtasks: [
          { title: 'Fensterrahmen säubern', done: 0 },
          { title: 'Glasreiniger & Tücher bereitlegen', done: 0 },
          { title: 'Fenster innen putzen', done: 0 },
          { title: 'Fenster außen putzen', done: 0 },
          { title: 'Fensterbänke wischen', done: 0 }
        ]
      },
      {
        id: 'ml-8',
        name: 'Betten frisch beziehen',
        emoji: '🛏️',
        frequent: false,
        description: 'Bettwäsche wechseln und Betten neu beziehen.',
        subtasks: [
          { title: 'Alte Bettwäsche abziehen', done: 0 },
          { title: 'Matratze lüften', done: 0 },
          { title: 'Frische Bettwäsche holen', done: 0 },
          { title: 'Betten neu beziehen', done: 0 },
          { title: 'Alte Wäsche in Wäschekorb', done: 0 }
        ]
      }
    ]
  },
  'kinder': {
    id: 'kinder',
    label: 'Kinder',
    emoji: '👶',
    tasks: [
      {
        id: 'ml-9',
        name: 'Kita/Schule-Termine koordinieren',
        emoji: '📅',
        frequent: true,
        description: 'Alle Termine rund um Kita und Schule im Blick behalten.',
        subtasks: [
          { title: 'Elternbriefe durchlesen', done: 0 },
          { title: 'Termine in Kalender eintragen', done: 0 },
          { title: 'Anmeldungen/Rückmeldungen', done: 0 },
          { title: 'Schulmaterialien checken', done: 0 },
          { title: 'Geld für Ausflüge bereitlegen', done: 0 }
        ]
      },
      {
        id: 'ml-10',
        name: 'Arzttermine für Kinder machen',
        emoji: '🏥',
        frequent: true,
        description: 'Kinderarzt- und Vorsorge-Termine organisieren.',
        subtasks: [
          { title: 'U-Untersuchung fällig? Prüfen', done: 0 },
          { title: 'Termin telefonisch vereinbaren', done: 0 },
          { title: 'Impfpass & Unterlagen bereitlegen', done: 0 },
          { title: 'Termin in Kalender eintragen', done: 0 },
          { title: 'Betreuung für Geschwister?', done: 0 }
        ]
      },
      {
        id: 'ml-11',
        name: 'Kleidung checken (Größe)',
        emoji: '👕',
        frequent: false,
        description: 'Kinderkleidung auf passende Größe prüfen und aussortieren.',
        subtasks: [
          { title: 'Aktuelle Größe messen', done: 0 },
          { title: 'Schrank durchgehen', done: 0 },
          { title: 'Zu kleine Sachen aussortieren', done: 0 },
          { title: 'Fehlende Teile notieren', done: 0 },
          { title: 'Saisonkleidung wechseln', done: 0 }
        ]
      },
      {
        id: 'ml-12',
        name: 'Schulessen bestellen/planen',
        emoji: '🍱',
        frequent: true,
        description: 'Mittagessen für Schule/Kita organisieren.',
        subtasks: [
          { title: 'Essensplan durchschauen', done: 0 },
          { title: 'Online Bestellung aufgeben', done: 0 },
          { title: 'Allergien/Unverträglichkeiten melden', done: 0 },
          { title: 'Guthaben auf Konto prüfen', done: 0 }
        ]
      },
      {
        id: 'ml-13',
        name: 'Hausaufgaben kontrollieren',
        emoji: '📚',
        frequent: true,
        description: 'Hausaufgaben begleiten und kontrollieren.',
        subtasks: [
          { title: 'Hausaufgabenheft checken', done: 0 },
          { title: 'Bei Aufgaben helfen', done: 0 },
          { title: 'Erledigtes unterschreiben', done: 0 },
          { title: 'Für Tests/Arbeiten lernen', done: 0 },
          { title: 'Materialien für morgen packen', done: 0 }
        ]
      },
      {
        id: 'ml-14',
        name: 'Freizeitaktivitäten organisieren',
        emoji: '⚽',
        frequent: false,
        description: 'Sport, Musik und andere Aktivitäten koordinieren.',
        subtasks: [
          { title: 'Trainingszeiten notieren', done: 0 },
          { title: 'Fahrdienst organisieren', done: 0 },
          { title: 'Sportkleidung/Ausrüstung checken', done: 0 },
          { title: 'Vereinsbeiträge zahlen', done: 0 },
          { title: 'Veranstaltungen/Auftritte planen', done: 0 }
        ]
      },
      {
        id: 'ml-15',
        name: 'Geburtstagsgeschenke besorgen',
        emoji: '🎁',
        frequent: false,
        description: 'Geschenke für Kindergeburtstage organisieren.',
        subtasks: [
          { title: 'Einladungen checken', done: 0 },
          { title: 'Kind nach Wünschen fragen', done: 0 },
          { title: 'Geschenk kaufen', done: 0 },
          { title: 'Geschenk einpacken', done: 0 },
          { title: 'Karte schreiben', done: 0 }
        ]
      }
    ]
  },
  'finanzen': {
    id: 'finanzen',
    label: 'Finanzen',
    emoji: '💰',
    tasks: [
      {
        id: 'ml-16',
        name: 'Rechnungen bezahlen',
        emoji: '💳',
        frequent: true,
        description: 'Offene Rechnungen prüfen und fristgerecht bezahlen.',
        subtasks: [
          { title: 'Post & E-Mails auf Rechnungen prüfen', done: 0 },
          { title: 'Fälligkeiten notieren', done: 0 },
          { title: 'Überweisungen ausführen', done: 0 },
          { title: 'Zahlungsbestätigungen ablegen', done: 0 }
        ]
      },
      {
        id: 'ml-17',
        name: 'Kontostand überprüfen',
        emoji: '🏦',
        frequent: true,
        description: 'Kontobewegungen kontrollieren und Budget im Blick behalten.',
        subtasks: [
          { title: 'Online-Banking öffnen', done: 0 },
          { title: 'Abbuchungen prüfen', done: 0 },
          { title: 'Unbekannte Buchungen klären', done: 0 },
          { title: 'Kontostand notieren', done: 0 }
        ]
      },
      {
        id: 'ml-18',
        name: 'Versicherungen vergleichen',
        emoji: '📋',
        frequent: false,
        description: 'Versicherungen jährlich prüfen und vergleichen.',
        subtasks: [
          { title: 'Aktuelle Policen zusammenstellen', done: 0 },
          { title: 'Vergleichsportale checken', done: 0 },
          { title: 'Angebote einholen', done: 0 },
          { title: 'Kündigungsfristen notieren', done: 0 },
          { title: 'Ggf. wechseln', done: 0 }
        ]
      },
      {
        id: 'ml-19',
        name: 'Steuererklärung vorbereiten',
        emoji: '📊',
        frequent: false,
        description: 'Unterlagen für die Steuererklärung sammeln.',
        subtasks: [
          { title: 'Lohnsteuerbescheinigungen sammeln', done: 0 },
          { title: 'Belege für Werbungskosten', done: 0 },
          { title: 'Sonderausgaben zusammenstellen', done: 0 },
          { title: 'Handwerkerrechnungen suchen', done: 0 },
          { title: 'ELSTER/Steuerberater Termin', done: 0 }
        ]
      },
      {
        id: 'ml-20',
        name: 'Budget planen',
        emoji: '📈',
        frequent: true,
        description: 'Monatliches Budget aufstellen und überwachen.',
        subtasks: [
          { title: 'Fixkosten auflisten', done: 0 },
          { title: 'Variable Kosten schätzen', done: 0 },
          { title: 'Sparziele festlegen', done: 0 },
          { title: 'Budget-App aktualisieren', done: 0 }
        ]
      },
      {
        id: 'ml-21',
        name: 'Abos checken & kündigen',
        emoji: '🔄',
        frequent: false,
        description: 'Laufende Abos überprüfen und unnötige kündigen.',
        subtasks: [
          { title: 'Kontoauszüge nach Abos durchsuchen', done: 0 },
          { title: 'Streaming-Dienste prüfen', done: 0 },
          { title: 'Zeitschriften & Apps checken', done: 0 },
          { title: 'Kündigungen schreiben', done: 0 },
          { title: 'Kündigungsbestätigungen ablegen', done: 0 }
        ]
      }
    ]
  },
  'gesundheit': {
    id: 'gesundheit',
    label: 'Gesundheit',
    emoji: '❤️',
    tasks: [
      {
        id: 'ml-22',
        name: 'Arzttermin vereinbaren',
        emoji: '👨‍⚕️',
        frequent: true,
        description: 'Termine beim Hausarzt oder Facharzt machen.',
        subtasks: [
          { title: 'Welcher Arzt wird benötigt?', done: 0 },
          { title: 'Überweisung vorhanden?', done: 0 },
          { title: 'Praxis anrufen', done: 0 },
          { title: 'Termin im Kalender eintragen', done: 0 },
          { title: 'Versichertenkarte bereitlegen', done: 0 }
        ]
      },
      {
        id: 'ml-23',
        name: 'Medikamente nachbestellen',
        emoji: '💊',
        frequent: true,
        description: 'Medikamentenvorrat prüfen und rechtzeitig nachbestellen.',
        subtasks: [
          { title: 'Hausapotheke durchgehen', done: 0 },
          { title: 'Rezeptpflichtige Medis notieren', done: 0 },
          { title: 'Rezept beim Arzt anfordern', done: 0 },
          { title: 'In Apotheke bestellen', done: 0 },
          { title: 'Abgelaufene Medis entsorgen', done: 0 }
        ]
      },
      {
        id: 'ml-24',
        name: 'Impftermine checken',
        emoji: '💉',
        frequent: false,
        description: 'Impfstatus prüfen und Auffrischungen planen.',
        subtasks: [
          { title: 'Impfpässe raussuchen', done: 0 },
          { title: 'STIKO-Empfehlungen checken', done: 0 },
          { title: 'Fällige Impfungen notieren', done: 0 },
          { title: 'Termine vereinbaren', done: 0 }
        ]
      },
      {
        id: 'ml-25',
        name: 'Zahnarzt-Kontrolltermin',
        emoji: '🦷',
        frequent: false,
        description: 'Halbjährliche Zahnarztkontrolle planen.',
        subtasks: [
          { title: 'Letzten Termin checken', done: 0 },
          { title: 'Praxis anrufen', done: 0 },
          { title: 'Bonusheft bereitlegen', done: 0 },
          { title: 'Termin in Kalender eintragen', done: 0 }
        ]
      },
      {
        id: 'ml-26',
        name: 'Vorsorgeuntersuchung planen',
        emoji: '🩺',
        frequent: false,
        description: 'Check-up und Vorsorgeuntersuchungen organisieren.',
        subtasks: [
          { title: 'Welche Vorsorge steht an?', done: 0 },
          { title: 'Ab 35: Check-up alle 3 Jahre', done: 0 },
          { title: 'Termin vereinbaren', done: 0 },
          { title: 'Nüchtern? Vorbereitung?', done: 0 }
        ]
      },
      {
        id: 'ml-27',
        name: 'Rezepte abholen',
        emoji: '📃',
        frequent: true,
        description: 'Rezepte vom Arzt abholen und einlösen.',
        subtasks: [
          { title: 'Rezept beim Arzt anfordern', done: 0 },
          { title: 'Rezept abholen', done: 0 },
          { title: 'Zur Apotheke gehen', done: 0 },
          { title: 'Medikamente einlagern', done: 0 }
        ]
      }
    ]
  },
  'termine': {
    id: 'termine',
    label: 'Termine & Events',
    emoji: '📆',
    tasks: [
      {
        id: 'ml-28',
        name: 'Familienkalender pflegen',
        emoji: '🗓️',
        frequent: true,
        description: 'Alle Familientermine im Kalender aktuell halten.',
        subtasks: [
          { title: 'Neue Termine eintragen', done: 0 },
          { title: 'Wiederkehrende Termine prüfen', done: 0 },
          { title: 'Mit Partner/Familie abgleichen', done: 0 },
          { title: 'Erinnerungen setzen', done: 0 }
        ]
      },
      {
        id: 'ml-29',
        name: 'Geburtstage nicht vergessen',
        emoji: '🎂',
        frequent: true,
        description: 'Geburtstage im Blick behalten und rechtzeitig planen.',
        subtasks: [
          { title: 'Kommende Geburtstage checken', done: 0 },
          { title: 'Geschenkideen sammeln', done: 0 },
          { title: 'Geschenk besorgen', done: 0 },
          { title: 'Karte schreiben', done: 0 },
          { title: 'Rechtzeitig gratulieren', done: 0 }
        ]
      },
      {
        id: 'ml-30',
        name: 'Feiertage planen',
        emoji: '🎄',
        frequent: false,
        description: 'Feiertage und besondere Anlässe vorbereiten.',
        subtasks: [
          { title: 'Familienbesuche koordinieren', done: 0 },
          { title: 'Menü planen', done: 0 },
          { title: 'Geschenke organisieren', done: 0 },
          { title: 'Dekoration vorbereiten', done: 0 },
          { title: 'Einkäufe planen', done: 0 }
        ]
      },
      {
        id: 'ml-31',
        name: 'Urlaub buchen',
        emoji: '✈️',
        frequent: false,
        description: 'Urlaubsplanung und Buchungen organisieren.',
        subtasks: [
          { title: 'Urlaubsziel recherchieren', done: 0 },
          { title: 'Unterkunft buchen', done: 0 },
          { title: 'Anreise planen (Flug/Bahn/Auto)', done: 0 },
          { title: 'Reisedokumente prüfen', done: 0 },
          { title: 'Urlaub beim AG einreichen', done: 0 },
          { title: 'Haustier-/Pflanzenbetreuung', done: 0 }
        ]
      },
      {
        id: 'ml-32',
        name: 'Elternabend/Meetings',
        emoji: '👥',
        frequent: true,
        description: 'Elternabende und wichtige Meetings vorbereiten.',
        subtasks: [
          { title: 'Termin im Kalender prüfen', done: 0 },
          { title: 'Kinderbetreuung organisieren', done: 0 },
          { title: 'Fragen vorbereiten', done: 0 },
          { title: 'Notizen machen', done: 0 },
          { title: 'Follow-up Aufgaben notieren', done: 0 }
        ]
      },
      {
        id: 'ml-33',
        name: 'Handwerker-Termine koordinieren',
        emoji: '🔧',
        frequent: false,
        description: 'Handwerkertermine planen und koordinieren.',
        subtasks: [
          { title: 'Angebote einholen', done: 0 },
          { title: 'Termin vereinbaren', done: 0 },
          { title: 'Jemand muss zu Hause sein', done: 0 },
          { title: 'Zugang ermöglichen', done: 0 },
          { title: 'Rechnung prüfen & bezahlen', done: 0 }
        ]
      }
    ]
  },
  'soziales': {
    id: 'soziales',
    label: 'Soziales & Familie',
    emoji: '👨‍👩‍👧‍👦',
    tasks: [
      {
        id: 'ml-34',
        name: 'Familie anrufen',
        emoji: '📞',
        frequent: true,
        description: 'Regelmäßiger Kontakt mit Familie und Verwandten.',
        subtasks: [
          { title: 'Eltern/Schwiegereltern anrufen', done: 0 },
          { title: 'Geschwister kontaktieren', done: 0 },
          { title: 'Großeltern nicht vergessen', done: 0 },
          { title: 'Nächsten Besuch planen', done: 0 }
        ]
      },
      {
        id: 'ml-35',
        name: 'Geschenke organisieren',
        emoji: '🎀',
        frequent: false,
        description: 'Geschenke für verschiedene Anlässe besorgen.',
        subtasks: [
          { title: 'Anlass checken (Geburtstag/Hochzeit...)', done: 0 },
          { title: 'Geschenkidee finden', done: 0 },
          { title: 'Geschenk kaufen', done: 0 },
          { title: 'Verpackung besorgen', done: 0 },
          { title: 'Geschenk einpacken', done: 0 }
        ]
      },
      {
        id: 'ml-36',
        name: 'Besuch planen',
        emoji: '🏡',
        frequent: false,
        description: 'Besuch von oder bei Familie/Freunden organisieren.',
        subtasks: [
          { title: 'Termin abstimmen', done: 0 },
          { title: 'Anreise planen', done: 0 },
          { title: 'Gastgeschenk besorgen', done: 0 },
          { title: 'Übernachtung? Packen!', done: 0 }
        ]
      },
      {
        id: 'ml-37',
        name: 'Karten/Briefe schreiben',
        emoji: '✉️',
        frequent: false,
        description: 'Persönliche Grüße per Post versenden.',
        subtasks: [
          { title: 'Passende Karte besorgen', done: 0 },
          { title: 'Persönlichen Text schreiben', done: 0 },
          { title: 'Adresse prüfen', done: 0 },
          { title: 'Briefmarke & Einwerfen', done: 0 }
        ]
      },
      {
        id: 'ml-38',
        name: 'Freunde einladen',
        emoji: '🥳',
        frequent: false,
        description: 'Treffen mit Freunden organisieren.',
        subtasks: [
          { title: 'Termin vorschlagen', done: 0 },
          { title: 'Einladungen versenden', done: 0 },
          { title: 'Essen/Getränke planen', done: 0 },
          { title: 'Wohnung vorbereiten', done: 0 },
          { title: 'Aktivität planen?', done: 0 }
        ]
      },
      {
        id: 'ml-39',
        name: 'Nachbarschaftshilfe',
        emoji: '🤝',
        frequent: false,
        description: 'Nachbarn helfen oder um Hilfe bitten.',
        subtasks: [
          { title: 'Hilfe anbieten/anfragen', done: 0 },
          { title: 'Termin absprechen', done: 0 },
          { title: 'Schlüssel/Zugang regeln', done: 0 },
          { title: 'Danke sagen/kleines Dankeschön', done: 0 }
        ]
      }
    ]
  },
  'auto-mobilitaet': {
    id: 'auto-mobilitaet',
    label: 'Auto & Mobilität',
    emoji: '🚗',
    tasks: [
      {
        id: 'ml-40',
        name: 'TÜV-Termin',
        emoji: '🔍',
        frequent: false,
        description: 'Hauptuntersuchung (TÜV) rechtzeitig planen.',
        subtasks: [
          { title: 'TÜV-Fälligkeit prüfen (Plakette)', done: 0 },
          { title: 'Termin bei TÜV/Werkstatt machen', done: 0 },
          { title: 'Fahrzeugpapiere bereitlegen', done: 0 },
          { title: 'Auto hinbringen', done: 0 },
          { title: 'Neue Plakette abholen', done: 0 }
        ]
      },
      {
        id: 'ml-41',
        name: 'Auto waschen',
        emoji: '🧼',
        frequent: false,
        description: 'Auto innen und außen reinigen.',
        subtasks: [
          { title: 'Waschanlage anfahren', done: 0 },
          { title: 'Programm wählen', done: 0 },
          { title: 'Innenraum aussaugen', done: 0 },
          { title: 'Armaturenbrett wischen', done: 0 },
          { title: 'Müll entsorgen', done: 0 }
        ]
      },
      {
        id: 'ml-42',
        name: 'Reifenwechsel organisieren',
        emoji: '🛞',
        frequent: false,
        description: 'Saisonalen Reifenwechsel planen.',
        subtasks: [
          { title: 'Termin bei Werkstatt machen', done: 0 },
          { title: 'Reifen aus Lager holen (wenn selbst)', done: 0 },
          { title: 'Profiltiefe prüfen lassen', done: 0 },
          { title: 'Ggf. neue Reifen bestellen', done: 0 },
          { title: 'Wechsel durchführen lassen', done: 0 }
        ]
      },
      {
        id: 'ml-43',
        name: 'Tanken / Laden',
        emoji: '⛽',
        frequent: true,
        description: 'Fahrzeug tanken oder Akku laden.',
        subtasks: [
          { title: 'Tankstand/Akkustand prüfen', done: 0 },
          { title: 'Günstige Tankstelle finden', done: 0 },
          { title: 'Tanken/Laden', done: 0 },
          { title: 'Rechnung/Beleg aufheben', done: 0 }
        ]
      },
      {
        id: 'ml-44',
        name: 'Inspektion planen',
        emoji: '🔧',
        frequent: false,
        description: 'Regelmäßige Wartung und Inspektion organisieren.',
        subtasks: [
          { title: 'Km-Stand/Intervall prüfen', done: 0 },
          { title: 'Werkstatt-Termin machen', done: 0 },
          { title: 'Kostenvoranschlag?', done: 0 },
          { title: 'Auto hinbringen', done: 0 },
          { title: 'Scheckheft abstempeln lassen', done: 0 }
        ]
      }
    ]
  },
  'sonstiges': {
    id: 'sonstiges',
    label: 'Sonstiges',
    emoji: '📌',
    tasks: [
      {
        id: 'ml-45',
        name: 'Haustier versorgen',
        emoji: '🐕',
        frequent: true,
        description: 'Tägliche Versorgung des Haustieres sicherstellen.',
        subtasks: [
          { title: 'Futter geben', done: 0 },
          { title: 'Wasser auffüllen', done: 0 },
          { title: 'Gassi gehen / Spielen', done: 0 },
          { title: 'Futter nachbestellen?', done: 0 },
          { title: 'Tierarzt-Termin fällig?', done: 0 }
        ]
      },
      {
        id: 'ml-46',
        name: 'Pflanzen gießen',
        emoji: '🌱',
        frequent: true,
        description: 'Zimmerpflanzen und Garten pflegen.',
        subtasks: [
          { title: 'Erde auf Feuchtigkeit prüfen', done: 0 },
          { title: 'Pflanzen gießen', done: 0 },
          { title: 'Düngen (alle 2 Wochen)', done: 0 },
          { title: 'Welke Blätter entfernen', done: 0 }
        ]
      },
      {
        id: 'ml-47',
        name: 'Passwörter aktualisieren',
        emoji: '🔐',
        frequent: false,
        description: 'Passwörter regelmäßig ändern und sicher aufbewahren.',
        subtasks: [
          { title: 'Passwort-Manager aktualisieren', done: 0 },
          { title: 'Wichtige Passwörter ändern', done: 0 },
          { title: '2-Faktor-Auth aktivieren', done: 0 },
          { title: 'Alte/ungenutzte Accounts löschen', done: 0 }
        ]
      },
      {
        id: 'ml-48',
        name: 'Dokumente sortieren',
        emoji: '📁',
        frequent: false,
        description: 'Wichtige Dokumente ordnen und ablegen.',
        subtasks: [
          { title: 'Post durchgehen', done: 0 },
          { title: 'Wichtiges abheften', done: 0 },
          { title: 'Alte Dokumente aussortieren', done: 0 },
          { title: 'Digital scannen & speichern', done: 0 },
          { title: 'Altpapier entsorgen', done: 0 }
        ]
      },
      {
        id: 'ml-49',
        name: 'Kleiderschrank ausmisten',
        emoji: '👚',
        frequent: false,
        description: 'Kleiderschrank durchgehen und ausmisten.',
        subtasks: [
          { title: 'Alle Teile durchgehen', done: 0 },
          { title: 'Nicht mehr getragenes aussortieren', done: 0 },
          { title: 'Kaputtes entsorgen', done: 0 },
          { title: 'Spenden vorbereiten', done: 0 },
          { title: 'Schrank neu organisieren', done: 0 }
        ]
      },
      {
        id: 'ml-50',
        name: 'Wichtige Anrufe erledigen',
        emoji: '☎️',
        frequent: true,
        description: 'Aufgeschobene Telefonate endlich erledigen.',
        subtasks: [
          { title: 'Liste der Anrufe machen', done: 0 },
          { title: 'Unterlagen bereitlegen', done: 0 },
          { title: 'Anrufe tätigen', done: 0 },
          { title: 'Ergebnisse notieren', done: 0 },
          { title: 'Follow-ups planen', done: 0 }
        ]
      }
    ]
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Alle Tasks als flache Liste
 */
export const ALL_MENTAL_LOAD_TASKS = Object.values(MENTAL_LOAD_CATALOG)
  .flatMap(cat => cat.tasks.map(task => ({
    ...task,
    categoryId: cat.id,
    categoryLabel: cat.label,
    categoryEmoji: cat.emoji
  })));

/**
 * Häufig verwendete Tasks
 */
export const getFrequentMentalLoadTasks = () => {
  return ALL_MENTAL_LOAD_TASKS.filter(task => task.frequent);
};

/**
 * Suche in Tasks
 */
export const searchMentalLoadTasks = (query) => {
  if (!query || query.length < 2) return [];

  const lowerQuery = query.toLowerCase();

  return ALL_MENTAL_LOAD_TASKS.filter(task =>
    task.name.toLowerCase().includes(lowerQuery) ||
    task.categoryLabel.toLowerCase().includes(lowerQuery) ||
    (task.description && task.description.toLowerCase().includes(lowerQuery))
  );
};

/**
 * Task nach ID finden
 */
export const getMentalLoadTaskById = (id) => {
  return ALL_MENTAL_LOAD_TASKS.find(task => task.id === id);
};

// ============================================================================
// EXPORTS
// ============================================================================
export default {
  MENTAL_LOAD_CATALOG,
  ALL_MENTAL_LOAD_TASKS,
  getFrequentMentalLoadTasks,
  searchMentalLoadTasks,
  getMentalLoadTaskById
};
